import React, { useState, useEffect } from 'react';
import { Eye, Type, Sparkles, Image as ImageIcon, Accessibility, X, RotateCcw, Volume2, Link as LinkIcon, Move, HelpCircle, AlignLeft } from 'lucide-react';

const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Accessibility Options State
  const [contrast, setContrast] = useState('standard'); // 'standard' | 'high' | 'monochrome'
  const [highlightLinks, setHighlightLinks] = useState(false);
  const [biggerText, setBiggerText] = useState('normal'); // 'normal' | 'large' | 'extra-large'
  const [textSpacing, setTextSpacing] = useState('default'); // 'default' | 'wide' | 'extra-wide'
  const [pauseMotion, setPauseMotion] = useState(false);
  const [hideImages, setHideImages] = useState(false);
  const [dyslexiaFont, setDyslexiaFont] = useState(false);
  const [bigCursor, setBigCursor] = useState(false);
  const [lineHeight, setLineHeight] = useState('default'); // 'default' | 'height-1.5' | 'height-2'
  const [textAlign, setTextAlign] = useState('default'); // 'default' | 'left' | 'center' | 'justify'
  const [screenReader, setScreenReader] = useState(false);
  const [boldText, setBoldText] = useState(false);

  // Apply changes to the HTML document body/root element
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    // --- 1. Contrast ---
    root.classList.remove('a11y-high-contrast', 'a11y-monochrome');
    if (contrast === 'high') root.classList.add('a11y-high-contrast');
    if (contrast === 'monochrome') root.classList.add('a11y-monochrome');

    // --- 2. Highlight Links ---
    if (highlightLinks) {
      root.classList.add('a11y-highlight-links');
    } else {
      root.classList.remove('a11y-highlight-links');
    }

    // --- 3. Bigger Text ---
    root.classList.remove('a11y-text-large', 'a11y-text-xlarge');
    if (biggerText === 'large') root.classList.add('a11y-text-large');
    if (biggerText === 'extra-large') root.classList.add('a11y-text-xlarge');

    // --- 4. Text Spacing ---
    root.classList.remove('a11y-spacing-wide', 'a11y-spacing-xwide');
    if (textSpacing === 'wide') root.classList.add('a11y-spacing-wide');
    if (textSpacing === 'extra-wide') root.classList.add('a11y-spacing-xwide');

    // --- 5. Pause Motion ---
    if (pauseMotion) {
      root.classList.add('a11y-pause-motion');
    } else {
      root.classList.remove('a11y-pause-motion');
    }
    window.dispatchEvent(new Event('a11y-pause-motion-changed'));

    // --- 6. Hide Images ---
    if (hideImages) {
      root.classList.add('a11y-hide-images');
    } else {
      root.classList.remove('a11y-hide-images');
    }

    // --- 7. Dyslexia Font ---
    if (dyslexiaFont) {
      body.classList.add('a11y-dyslexia');
    } else {
      body.classList.remove('a11y-dyslexia');
    }

    // --- 8. Big Cursor ---
    if (bigCursor) {
      body.classList.add('a11y-big-cursor');
    } else {
      body.classList.remove('a11y-big-cursor');
    }

    // --- 9. Line Height ---
    root.classList.remove('a11y-line-15', 'a11y-line-20');
    if (lineHeight === 'height-1.5') root.classList.add('a11y-line-15');
    if (lineHeight === 'height-2') root.classList.add('a11y-line-20');

    // --- 10. Text Align ---
    root.classList.remove('a11y-align-left', 'a11y-align-center', 'a11y-align-justify');
    if (textAlign === 'left') root.classList.add('a11y-align-left');
    if (textAlign === 'center') root.classList.add('a11y-align-center');
    if (textAlign === 'justify') root.classList.add('a11y-align-justify');

    // --- 11. Bold Text ---
    if (boldText) {
      body.classList.add('a11y-bold');
    } else {
      body.classList.remove('a11y-bold');
    }

  }, [contrast, highlightLinks, biggerText, textSpacing, pauseMotion, hideImages, dyslexiaFont, bigCursor, lineHeight, textAlign, boldText]);

  // Screen Reader / Text-to-Speech logic
  useEffect(() => {
    if (!screenReader) {
      window.speechSynthesis.cancel();
      return;
    }

    const handleMouseOver = (e) => {
      const target = e.target;
      // Get text content of elements when hovered
      if (['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'SPAN', 'A', 'BUTTON', 'LABEL', 'LI'].includes(target.tagName)) {
        const text = target.innerText || target.alt || '';
        if (text) {
          window.speechSynthesis.cancel();
          const utterance = new SpeechSynthesisUtterance(text);
          utterance.rate = 1.0;
          window.speechSynthesis.speak(utterance);
        }
      }
    };

    document.addEventListener('mouseover', handleMouseOver);
    return () => {
      document.removeEventListener('mouseover', handleMouseOver);
      window.speechSynthesis.cancel();
    };
  }, [screenReader]);

  const handleReset = () => {
    setContrast('standard');
    setHighlightLinks(false);
    setBiggerText('normal');
    setTextSpacing('default');
    setPauseMotion(false);
    setHideImages(false);
    setDyslexiaFont(false);
    setBigCursor(false);
    setLineHeight('default');
    setTextAlign('default');
    setScreenReader(false);
    setBoldText(false);
    window.speechSynthesis.cancel();
  };

  return (
    <>
      {/* Floating Action Button (Bottom Left) */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Open Accessibility Options"
        className="fixed bottom-6 left-6 z-[999] w-12 h-12 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-full flex items-center justify-center shadow-2xl border border-white/20 transition-transform duration-300 hover:scale-110 focus:ring-4 focus:ring-amber-500/20 outline-none"
      >
        <Accessibility className="w-6 h-6 stroke-[2.5]" />
      </button>

      {/* Accessibility Side/Overlay Modal Panel */}
      {isOpen && (
        <>
          {/* Backdrop layer for click-outside */}
          <div 
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 z-[998] bg-transparent"
          />
          
          {/* Floating modal panel positioned directly above the button */}
          <div 
            className="fixed bottom-20 left-6 z-[999] a11y-exclude a11y-exclude-container bg-slate-950 text-white rounded-3xl w-full max-w-sm border border-slate-800 shadow-2xl flex flex-col overflow-hidden h-fit max-h-[75vh] animate-in slide-in-from-bottom duration-300"
          >
            
            {/* Header */}
            <div className="p-5 bg-slate-900/80 border-b border-slate-800 flex justify-between items-center shrink-0">
              <div>
                <h2 className="text-sm font-black uppercase tracking-widest text-white leading-none">Accessibility Options</h2>
                <p className="text-[9px] font-bold text-amber-500 uppercase tracking-wider mt-1.5 leading-none">Customize Screen Layout & Tools</p>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-white/10 rounded-lg transition-colors text-slate-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Scrollable controls container */}
            <div className="flex-1 p-5 overflow-y-auto space-y-4 max-h-[70vh]">
              {/* Badge Area */}
              <div className="flex gap-2 mb-2">
                <span className="px-2.5 py-1 bg-amber-500/10 text-amber-500 text-[8px] font-black uppercase tracking-widest rounded-lg border border-amber-500/20">
                  Premium Controls Active
                </span>
                <span className="px-2.5 py-1 bg-emerald-500/10 text-emerald-500 text-[8px] font-black uppercase tracking-widest rounded-lg border border-emerald-500/20">
                  WCAG 2.1 Compliant
                </span>
              </div>

              {/* Grid of Interactive Options */}
              <div className="grid grid-cols-2 gap-3">
                {/* 1. Contrast Toggle */}
                <button
                  onClick={() => setContrast(c => c === 'standard' ? 'high' : c === 'high' ? 'monochrome' : 'standard')}
                  className={`p-4 rounded-2xl border text-center flex flex-col items-center justify-center gap-2 transition-all ${
                    contrast !== 'standard' 
                      ? 'bg-amber-500 text-slate-950 border-amber-500' 
                      : 'bg-slate-900/60 border-slate-800 text-slate-300 hover:border-slate-700'
                  }`}
                >
                  <Eye className="w-5 h-5" />
                  <span className="text-[9px] font-black uppercase tracking-widest">Contrast</span>
                  <span className="text-[8px] font-bold opacity-75 uppercase">{contrast}</span>
                </button>

                {/* 2. Highlight Links */}
                <button
                  onClick={() => setHighlightLinks(!highlightLinks)}
                  className={`p-4 rounded-2xl border text-center flex flex-col items-center justify-center gap-2 transition-all ${
                    highlightLinks 
                      ? 'bg-amber-500 text-slate-950 border-amber-500' 
                      : 'bg-slate-900/60 border-slate-800 text-slate-300 hover:border-slate-700'
                  }`}
                >
                  <LinkIcon className="w-5 h-5" />
                  <span className="text-[9px] font-black uppercase tracking-widest">Highlight Links</span>
                  <span className="text-[8px] font-bold opacity-75 uppercase">{highlightLinks ? 'Enabled' : 'Disabled'}</span>
                </button>

                {/* 3. Bigger Text */}
                <button
                  onClick={() => setBiggerText(t => t === 'normal' ? 'large' : t === 'large' ? 'extra-large' : 'normal')}
                  className={`p-4 rounded-2xl border text-center flex flex-col items-center justify-center gap-2 transition-all ${
                    biggerText !== 'normal' 
                      ? 'bg-amber-500 text-slate-950 border-amber-500' 
                      : 'bg-slate-900/60 border-slate-800 text-slate-300 hover:border-slate-700'
                  }`}
                >
                  <Type className="w-5 h-5" />
                  <span className="text-[9px] font-black uppercase tracking-widest">Bigger Text</span>
                  <span className="text-[8px] font-bold opacity-75 uppercase">{biggerText}</span>
                </button>

                {/* 4. Text Spacing */}
                <button
                  onClick={() => setTextSpacing(s => s === 'default' ? 'wide' : s === 'wide' ? 'extra-wide' : 'default')}
                  className={`p-4 rounded-2xl border text-center flex flex-col items-center justify-center gap-2 transition-all ${
                    textSpacing !== 'default' 
                      ? 'bg-amber-500 text-slate-950 border-amber-500' 
                      : 'bg-slate-900/60 border-slate-800 text-slate-300 hover:border-slate-700'
                  }`}
                >
                  <HelpCircle className="w-5 h-5" />
                  <span className="text-[9px] font-black uppercase tracking-widest">Text Spacing</span>
                  <span className="text-[8px] font-bold opacity-75 uppercase">{textSpacing}</span>
                </button>

                {/* 5. Pause Motion */}
                <button
                  onClick={() => setPauseMotion(!pauseMotion)}
                  className={`p-4 rounded-2xl border text-center flex flex-col items-center justify-center gap-2 transition-all ${
                    pauseMotion 
                      ? 'bg-amber-500 text-slate-950 border-amber-500' 
                      : 'bg-slate-900/60 border-slate-800 text-slate-300 hover:border-slate-700'
                  }`}
                >
                  <Move className="w-5 h-5" />
                  <span className="text-[9px] font-black uppercase tracking-widest">Pause Motion</span>
                  <span className="text-[8px] font-bold opacity-75 uppercase">{pauseMotion ? 'Animations Off' : 'Animations On'}</span>
                </button>

                {/* 6. Hide Images */}
                <button
                  onClick={() => setHideImages(!hideImages)}
                  className={`p-4 rounded-2xl border text-center flex flex-col items-center justify-center gap-2 transition-all ${
                    hideImages 
                      ? 'bg-amber-500 text-slate-950 border-amber-500' 
                      : 'bg-slate-900/60 border-slate-800 text-slate-300 hover:border-slate-700'
                  }`}
                >
                  <ImageIcon className="w-5 h-5" />
                  <span className="text-[9px] font-black uppercase tracking-widest">Hide Images</span>
                  <span className="text-[8px] font-bold opacity-75 uppercase">{hideImages ? 'Images Hidden' : 'Images Shown'}</span>
                </button>

                {/* 7. Dyslexia Font */}
                <button
                  onClick={() => setDyslexiaFont(!dyslexiaFont)}
                  className={`p-4 rounded-2xl border text-center flex flex-col items-center justify-center gap-2 transition-all ${
                    dyslexiaFont 
                      ? 'bg-amber-500 text-slate-950 border-amber-500' 
                      : 'bg-slate-900/60 border-slate-800 text-slate-300 hover:border-slate-700'
                  }`}
                >
                  <Type className="w-5 h-5 font-mono" />
                  <span className="text-[9px] font-black uppercase tracking-widest">Dyslexia Font</span>
                  <span className="text-[8px] font-bold opacity-75 uppercase">{dyslexiaFont ? 'Dyslexia Font' : 'Standard'}</span>
                </button>

                {/* 8. Big Cursor */}
                <button
                  onClick={() => setBigCursor(!bigCursor)}
                  className={`p-4 rounded-2xl border text-center flex flex-col items-center justify-center gap-2 transition-all ${
                    bigCursor 
                      ? 'bg-amber-500 text-slate-950 border-amber-500' 
                      : 'bg-slate-900/60 border-slate-800 text-slate-300 hover:border-slate-700'
                  }`}
                >
                  <Move className="w-5 h-5 rotate-45" />
                  <span className="text-[9px] font-black uppercase tracking-widest">Big Cursor</span>
                  <span className="text-[8px] font-bold opacity-75 uppercase">{bigCursor ? 'Big Cursor' : 'Normal'}</span>
                </button>

                {/* 9. Line Height */}
                <button
                  onClick={() => setLineHeight(h => h === 'default' ? 'height-1.5' : h === 'height-1.5' ? 'height-2' : 'default')}
                  className={`p-4 rounded-2xl border text-center flex flex-col items-center justify-center gap-2 transition-all ${
                    lineHeight !== 'default' 
                      ? 'bg-amber-500 text-slate-950 border-amber-500' 
                      : 'bg-slate-900/60 border-slate-800 text-slate-300 hover:border-slate-700'
                  }`}
                >
                  <AlignLeft className="w-5 h-5" />
                  <span className="text-[9px] font-black uppercase tracking-widest">Line Height</span>
                  <span className="text-[8px] font-bold opacity-75 uppercase">{lineHeight}</span>
                </button>

                {/* 10. Text Align */}
                <button
                  onClick={() => setTextAlign(a => a === 'default' ? 'left' : a === 'left' ? 'center' : a === 'center' ? 'justify' : 'default')}
                  className={`p-4 rounded-2xl border text-center flex flex-col items-center justify-center gap-2 transition-all ${
                    textAlign !== 'default' 
                      ? 'bg-amber-500 text-slate-950 border-amber-500' 
                      : 'bg-slate-900/60 border-slate-800 text-slate-300 hover:border-slate-700'
                  }`}
                >
                  <AlignLeft className="w-5 h-5" />
                  <span className="text-[9px] font-black uppercase tracking-widest">Text Align</span>
                  <span className="text-[8px] font-bold opacity-75 uppercase">{textAlign}</span>
                </button>

                {/* 11. Screen Reader */}
                <button
                  onClick={() => setScreenReader(!screenReader)}
                  className={`p-4 rounded-2xl border text-center flex flex-col items-center justify-center gap-2 transition-all ${
                    screenReader 
                      ? 'bg-amber-500 text-slate-950 border-amber-500' 
                      : 'bg-slate-900/60 border-slate-800 text-slate-300 hover:border-slate-700'
                  }`}
                >
                  <Volume2 className="w-5 h-5" />
                  <span className="text-[9px] font-black uppercase tracking-widest">Screen Reader</span>
                  <span className="text-[8px] font-bold opacity-75 uppercase">{screenReader ? 'Speech On' : 'Speech Off'}</span>
                </button>

                {/* 12. Bold Text */}
                <button
                  onClick={() => setBoldText(!boldText)}
                  className={`p-4 rounded-2xl border text-center flex flex-col items-center justify-center gap-2 transition-all ${
                    boldText 
                      ? 'bg-amber-500 text-slate-950 border-amber-500' 
                      : 'bg-slate-900/60 border-slate-800 text-slate-300 hover:border-slate-700'
                  }`}
                >
                  <Type className="w-5 h-5 stroke-[3]" />
                  <span className="text-[9px] font-black uppercase tracking-widest">Bold Text</span>
                  <span className="text-[8px] font-bold opacity-75 uppercase">{boldText ? 'Bold' : 'Standard'}</span>
                </button>
              </div>
            </div>

            {/* Footer containing Reset button */}
            <div className="p-4 bg-slate-900/80 border-t border-slate-800 flex gap-2 shrink-0">
              <button 
                onClick={handleReset}
                className="w-full py-3 bg-transparent border border-slate-800 text-slate-300 rounded-2xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-1.5 hover:bg-white/5 transition-all"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Reset All Accessibility Settings
              </button>
            </div>
            
          </div>
        </>
      )}
    </>
  );
};

export default AccessibilityWidget;
